import { Button } from 'src/v2/commonComponents/Button';
import styles from './ExternalModelItem.module.scss';
import { Check } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import { UserTier } from 'src/types';
import { LabelNew } from 'src/components/LabelNew';
import { LabelUpgrade } from 'src/components/LabelUpgrade';
import { sendGTMEvent } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { AppRoutes, GTMEvent } from 'src/types';
import classNames from 'classnames';

import {
  useGetUser,
  useRequestUpgradeModal,
  useSubscriptionInfo,
  useTeamInfo,
} from 'src/hooks';
import { toast } from 'react-toastify';
import {
  APPLE_PAYMENT_UPGRADE_ERROR_TEXT,
  SUBSCRIPTION_PENDING_CHANGES_NOTIFICATION_MESSAGE,
} from 'src/constants';

export interface ExternalModelItemProps {
  id: string;
  icon: JSX.Element;
  title: string;
  selected?: boolean;
  availableIn?: UserTier;
  onClick?: (id: string) => void;
  showRegenerate?: boolean;
  description?: string | null;
  isNew?: boolean;
}

export const ExternalModelItem = ({
  id,
  icon,
  title,
  selected,
  availableIn,
  onClick,
  showRegenerate,
  description,
  isNew,
}: ExternalModelItemProps) => {
  const handleClick = () => {
    if (onClick) {
      onClick(id);
    }
  };
  const { isTeamsTier } = useGetUser();
  const navigate = useNavigate();
  const { subscriptionInfo, isAppleOrAmpPayment } = useSubscriptionInfo();
  const { onOpenRequestUpgradeModal } = useRequestUpgradeModal();
  const { isOwner, isMember, isAdmin } = useTeamInfo();

  const handleUpgradeClick = () => {
    if (
      !!subscriptionInfo?.is_cancelled ||
      !!subscriptionInfo?.has_scheduled_downgrade ||
      !!subscriptionInfo?.has_scheduled_plan_change ||
      !!subscriptionInfo?.processing_plan_change ||
      isAppleOrAmpPayment
    ) {
      toast.error(
        isAppleOrAmpPayment
          ? APPLE_PAYMENT_UPGRADE_ERROR_TEXT
          : SUBSCRIPTION_PENDING_CHANGES_NOTIFICATION_MESSAGE,
      );
    } else {
      if (isTeamsTier) {
        if (isOwner) {
          navigate(AppRoutes.PAYMENT);
        } else {
          onOpenRequestUpgradeModal();
        }
      } else {
        navigate(AppRoutes.PAYMENT);
      }
    }
    sendGTMEvent(GTMEvent.UPGRADE_BUTTON_CLICKS);
  };

  const shouldShowUpgrade = !!availableIn && !isMember && !isAdmin;

  return (
    <Button
      onClick={!!availableIn ? handleUpgradeClick : handleClick}
      className={classNames(styles.root, {
        [styles.disabled]: !!availableIn,
      })}
      color="transparent"
      data-e2e={id}
    >
      <span className={styles.titleWrapper}>
        <div className={styles.leftSideWrapper}>
          {icon}
          <div className={styles.textWrapper}>
            <span className={styles.title}>{title}</span>
            {description && (
              <span className={styles.description}>{description}</span>
            )}
          </div>
        </div>

        {isNew && !showRegenerate && !availableIn && <LabelNew />}
        {shouldShowUpgrade && <LabelUpgrade />}
      </span>

      {selected && !showRegenerate && <Check size={SVG_SIZE_M} />}
    </Button>
  );
};
